/**
 * Task Contexts: URLs, labels and where-am-I parsing, shared by the worker and the frontend.
 */
import {
  isTaskContextType,
  type TaskContextRef,
  type TaskContextType,
} from './types/tasks';

export const TASK_CONTEXT_TYPE_LABELS: Record<TaskContextType, string> = {
  page: 'Page',
  url: 'Web page',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuidLike(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/** Where a Context opens: a page in the app, or the web page itself. */
export function taskContextUrl(ref: TaskContextRef): string {
  switch (ref.type) {
    case 'page':
      return ref.id.startsWith('/') ? ref.id : `/${ref.id}`;
    case 'url':
      return ref.id;
  }
}

/** Context types whose id is text rather than a uuid, with no row behind them. */
export function isAddressContextType(type: TaskContextType): type is 'page' | 'url' {
  return type === 'page' || type === 'url';
}

/**
 * A web page outside the app as a `url` Context id: http(s) only, without the #fragment. Null when it
 * isn't a web address. Tasks on a page are found by the address without its query (as `page` does).
 */
export function webPageContextId(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  url.hash = '';
  const id = url.toString();
  return id.length <= 1000 ? id : null;
}

/** What the Tasks panel shows for the page you're on. */
export interface TaskPageScope {
  /** Pre-filled Context for quick-add. */
  primary: TaskContextRef;
  /** Tasks on any of these (or whose parent is one of these) are listed. */
  scope: TaskContextRef[];
}

/** Page contexts store path + query; the panel matches them on the path alone. */
export function pageContextId(pathname: string, search: string): string {
  const query = search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : '';
  return `${pathname}${query}`;
}

export function pageContextPath(id: string): string {
  const q = id.indexOf('?');
  return q === -1 ? id : id.slice(0, q);
}

/** Work out the Context of an app URL: every page is a Page context for now. */
export function taskScopeFromLocation(pathname: string, search: string): TaskPageScope {
  return {
    primary: { type: 'page', id: pageContextId(pathname, search) },
    scope: [{ type: 'page', id: pathname }],
  };
}

/** Parse an app link (e.g. pasted into Slack) into a Context; null when it isn't one of the app's pages. */
export function taskContextFromUrl(raw: string, appOrigin: string): TaskContextRef | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  let origin: string;
  try {
    origin = new URL(appOrigin).origin;
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (url.pathname === '/' || url.pathname.startsWith('/tasks')) return null;
  return taskScopeFromLocation(url.pathname, url.search).primary;
}

/* ------------------------------------------------------------------ */
/* Mentions: `@[Label](type:id)` in descriptions and chat.             */
/* ------------------------------------------------------------------ */

/** `machineId` (SF-T-174): the AI builder on a chosen fleet machine. */
export type TaskMentionTarget = { kind: 'user'; id: string } | { kind: 'context'; ref: TaskContextRef } | { kind: 'ai'; machineId?: string };

/** The AI builder (the task loop) as a mention: `@[AI builder](ai:builder)`. */
export const AI_BUILDER_MENTION_TYPE = 'ai';
export const AI_BUILDER_MENTION_ID = 'builder';

/**
 * SF-T-174: the AI builder on a chosen machine, `@[AI builder on jackalope](ai:builder@<machine id>)`: the
 * comment asks the AI builder and sets the task's machine for its runs.
 */
export function aiBuilderMentionId(machineId?: string | null): string {
  return machineId ? `${AI_BUILDER_MENTION_ID}@${machineId}` : AI_BUILDER_MENTION_ID;
}

/** The machine in an AI builder mention's id: null for a plain `builder`, undefined when it isn't one. */
export function aiBuilderMentionMachine(id: string): string | null | undefined {
  if (id === AI_BUILDER_MENTION_ID) return null;
  const machineId = id.startsWith(`${AI_BUILDER_MENTION_ID}@`) ? id.slice(AI_BUILDER_MENTION_ID.length + 1) : '';
  return isUuidLike(machineId) ? machineId : undefined;
}

export interface TaskMentionToken {
  label: string;
  target: TaskMentionTarget;
  start: number;
  end: number;
}

const MENTION_RE = /@\[([^\]\n]{1,200})\]\(([a-z_]+):([^)\s]{1,500})\)/g;

export function mentionMarkup(label: string, type: 'user' | 'ai' | TaskContextType, id: string): string {
  const clean = label.replace(/[[\]\n]/g, ' ').trim() || type;
  return `@[${clean}](${type}:${id})`;
}

export function parseMentionTokens(text: string): TaskMentionToken[] {
  const tokens: TaskMentionToken[] = [];
  for (const match of text.matchAll(MENTION_RE)) {
    const [whole, label, type, id] = match;
    const start = match.index ?? 0;
    if (type === 'user') {
      if (!isUuidLike(id)) continue;
      tokens.push({ label, target: { kind: 'user', id }, start, end: start + whole.length });
    } else if (type === AI_BUILDER_MENTION_TYPE) {
      const machineId = aiBuilderMentionMachine(id);
      if (machineId === undefined) continue;
      tokens.push({ label, target: machineId ? { kind: 'ai', machineId } : { kind: 'ai' }, start, end: start + whole.length });
    } else if (isTaskContextType(type) && !isAddressContextType(type)) {
      if (!isUuidLike(id)) continue;
      tokens.push({ label, target: { kind: 'context', ref: { type, id } }, start, end: start + whole.length });
    }
  }
  return tokens;
}

export function parseMentions(text: string): {
  userIds: string[];
  contexts: Array<TaskContextRef & { title: string }>;
  /** The AI builder is @'d: on its own, as it isn't a person (no follower or bell). */
  aiBuilder: boolean;
  /** SF-T-174: the machine an "AI builder on <machine>" mention chose (the last one, when several); else null. */
  aiMachineId: string | null;
} {
  const userIds = new Set<string>();
  const contexts = new Map<string, TaskContextRef & { title: string }>();
  let aiBuilder = false;
  let aiMachineId: string | null = null;
  for (const token of parseMentionTokens(text)) {
    if (token.target.kind === 'user') userIds.add(token.target.id);
    else if (token.target.kind === 'ai') {
      aiBuilder = true;
      if (token.target.machineId) aiMachineId = token.target.machineId;
    }
    else {
      const { ref } = token.target;
      contexts.set(`${ref.type}:${ref.id}`, { ...ref, title: token.label });
    }
  }
  return { userIds: [...userIds], contexts: [...contexts.values()], aiBuilder, aiMachineId };
}

/** Plain text for notifications: `@[Sam](user:…)` becomes `@Sam`. */
export function mentionsToPlainText(text: string): string {
  return text.replace(MENTION_RE, (_whole, label: string) => `@${label}`);
}

export function taskContextKey(ref: TaskContextRef): string {
  return `${ref.type}:${ref.id}`;
}
